// 可视化编辑器数据类型定义

export type ElementCategory =
  | 'character'
  | 'prop'
  | 'background'
  | 'decoration'
  | 'text';

export type Interpolation =
  | 'none'
  | 'linear'
  | 'easeIn'
  | 'easeOut'
  | 'easeInOut'
  | 'spring'
  | 'bounce';

export interface Vec3Tuple {
  x: number;
  y: number;
  z: number;
}

export interface EditorElement {
  id: string;
  name: string;
  category: ElementCategory;
  imageUrl?: string;
  color?: string;
  position: Vec3Tuple;
  rotation: Vec3Tuple;
  scale: Vec3Tuple;
  visible: boolean;
  opacity: number;
  // 叙事元数据（NME 驱动）
  narrative?: {
    role: string;        // main_character / prop / background / decoration
    emotion: string;     // happy / sad / curious ...
    texture: string;     // soft / rough / shiny ...
    physics: string;     // rigid / springy / floating ...
    behavior: string;    // idle / float / pulse / follow ...
    intensity: number;   // 0-1
    interaction: string; // tap / drag / shake / none
  };
  // 关键帧
  keyframes?: Keyframe[];
}

export interface Keyframe {
  id: string;
  time: number;          // 秒
  position?: Vec3Tuple;
  rotation?: Vec3Tuple;
  scale?: Vec3Tuple;
  opacity?: number;
  easing: Interpolation;
}

export interface StoryText {
  id: string;
  content: string;
  language: string;      // zh / en / ja / fr / es
  position: Vec3Tuple;
  fontSize: number;
  color: string;
  bold: boolean;
  align: 'left' | 'center' | 'right';
  // 逐字高亮时间戳（由 TTS 后端生成）
  wordTimestamps?: { word: string; start: number; end: number }[];
  voice?: string;
}

export interface EditorPage {
  id: string;
  title: string;
  background: {
    color: string;
    imageUrl?: string;
    sceneType: string;   // moonlit_forest / ocean_floor ...
    timeOfDay: string;
    mood: string;
  };
  lighting: {
    preset: string;      // moonlight / firefly / dusk / soft-glow ...
    intensity: number;
    fog: boolean;
  };
  particles: {
    preset: string;      // fireflies / snow / leaves / sparkles ...
    count: number;
    enabled: boolean;
  };
  audio?: {
    url?: string;
    narration?: string;
    language?: string;
  };
  elements: EditorElement[];
  texts: StoryText[];
  duration: number;      // 页面总时长（秒）
}

export interface EditorBook {
  id: string;
  title: string;
  cover: string;
  author: string;
  language: string;
  created: string;
  updated: string;
  pages: EditorPage[];
}

export interface HistoryEntry {
  book: EditorBook;
  label: string;
  timestamp: number;
}

export interface EditorState {
  book: EditorBook | null;
  currentPageId: string | null;
  selectedElementId: string | null;
  playing: boolean;
  playhead: number;          // 当前播放时间（秒）
  undoStack: HistoryEntry[];
  redoStack: HistoryEntry[];
}

export interface PresetLibrary {
  lighting: {
    name: string;
    label: string;
    ambient: string;
    directional: string;
    point?: string;
    fog?: boolean;
  }[];
  particles: {
    name: string;
    label: string;
    color: string;
    speed: number;
  }[];
  elements: {
    name: string;
    label: string;
    category: ElementCategory;
    description: string;
  }[];
}

export const LIGHTING_PRESETS: PresetLibrary['lighting'] = [
  { name: 'soft-glow', label: '柔和辉光', ambient: '#fff5e0', directional: '#ffd9a0', fog: true },
  { name: 'moonlight', label: '月光', ambient: '#b8c8e8', directional: '#8a9cc8', fog: true },
  { name: 'firefly', label: '萤火微光', ambient: '#ffe9b0', directional: '#ffb347', point: '#ffd76e', fog: true },
  { name: 'dusk', label: '暮色', ambient: '#f0c8a8', directional: '#c98a5a', fog: false },
  { name: 'aurora', label: '极光', ambient: '#c8e8d8', directional: '#7ac8a8', point: '#5ad0c8', fog: true },
  { name: 'sunrise', label: '晨曦', ambient: '#ffe8d0', directional: '#ffb88a', fog: false },
];

export const PARTICLE_PRESETS: PresetLibrary['particles'] = [
  { name: 'fireflies', label: '萤火虫', color: '#ffe27a', speed: 0.5 },
  { name: 'snow', label: '飘雪', color: '#ffffff', speed: 0.8 },
  { name: 'leaves', label: '落叶', color: '#7ac87a', speed: 0.6 },
  { name: 'sparkles', label: '星光', color: '#fff2c8', speed: 0.4 },
  { name: 'bubbles', label: '泡泡', color: '#a8d8f0', speed: 0.7 },
  { name: 'pollen', label: '花粉', color: '#f0d8a8', speed: 0.3 },
];

export const BUILDING_BLOCKS: PresetLibrary['elements'] = [
  { name: 'toy', label: '玩具熊', category: 'character', description: '主要角色，可赋予情绪与交互' },
  { name: 'moon', label: '月亮', category: 'prop', description: '核心道具，发光特效' },
  { name: 'tree', label: '大树', category: 'prop', description: '支撑性物件' },
  { name: 'flower', label: '花朵', category: 'decoration', description: '装饰层，粒子联动' },
  { name: 'cloud', label: '云朵', category: 'background', description: '背景层，缓慢飘动' },
  { name: 'text', label: '文本', category: 'text', description: '旁白/字幕，逐字高亮' },
];